import { css } from "@emotion/react";
import { Session } from "next-auth";
import { signIn, signOut, useSession } from "next-auth/react";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { match } from "ts-pattern";
import { z } from "zod";

import { flexRow } from "~/lib/client/design";
import { useGraphqlClientWithAuth } from "~/lib/client/hooks/useGraphqlClient";
import { useAppContext } from "~/pages/_app";

const YouTubeAlignment = () => {
  return (
    <div>
      <h3>YouTubeアカウント</h3>
      <YouTubeChannelInfo />
    </div>
  );
};

const API_KEY = "AIzaSyCILTu_ZdR-10iGeS6YLG7Ivdl-AQJWL6k";

async function fetchChannelInfo(accessToken: string) {
  const url = `https://youtube.googleapis.com/youtube/v3/channels?part=id&key=${API_KEY}&mine=true`;
  return await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
    },
  })
    .then(async (res) => {
      if (!res.ok) {
        console.error("Network response was not ok");
        throw new Error("Network response was not ok");
      }
      const data = await res.json();
      return youtubeChannelListResponseSchema.parse(data);
    })
    .catch((e) => {
      console.error(e);
      return null;
    });
}

function useYoutubeChannelInfo() {
  const { currentUser } = useAppContext();
  const [channelInfo, setChannelInfo] = useState<
    YoutubeChannelInfo | null | undefined
  >(undefined);
  const { data: session } = useSession();
  const accessToken = (session as any)?.accessToken;

  const fetcher = useCallback(
    async (accessToken: string) => await fetchChannelInfo(accessToken),
    []
  );

  useEffect(() => {
    const parsedAccessToken = z.string().safeParse(accessToken);
    if (!currentUser) return;
    if (!parsedAccessToken.success) {
      console.error(`access token is not string`, { parsedAccessToken });
      return;
    }

    fetcher(parsedAccessToken.data).then(setChannelInfo);
  }, [accessToken, currentUser, fetcher]);

  return { channelInfo, fetcher };
}

const youtubeResourceKinds = [
  "youtube#channelListResponse",
  "youtube#channel",
] as const;

// {
//  "kind":"youtube#channelListResponse",
//  "etag":"7kG00pZwtVfYuAF_1MNBvSSpybg",
//  "pageInfo":{
//    "totalResults":1,
//    "resultsPerPage":5
//  },
//  "items":[{
//    "kind":"youtube#channel",
//    "etag":"GmtgZEm-umBc1VefSgJrTy-aiEM",
//    "id":"UCg2mYGK5cnDEuHXg4lt2Uig"
//  }]
// }
const youtubeResourceSchema = z.object({
  kind: z.enum(youtubeResourceKinds),
  etag: z.string(),
});

const youtubeChannelItemSchema = z.object({
  kind: z.literal("youtube#channel"),
  etag: z.string(),
  id: z.string(),
});

const pageInformationSchema = z.object({
  totalResults: z.number(),
  resultsPerPage: z.number(),
});

const youtubeChannelListResponseSchema = youtubeResourceSchema.extend({
  kind: z.literal("youtube#channelListResponse"),
  etag: z.string(),
  pageInfo: pageInformationSchema,
  items: z.array(youtubeChannelItemSchema),
});

type YoutubeChannelInfo = z.infer<typeof youtubeChannelListResponseSchema>;

const YouTubeChannelInfo = () => {
  const session = useSession();

  return (
    <>
      <div css={informationContainerStyle}>
        {match(session)
          .with({ status: "loading" }, () => null)
          .with({ status: "unauthenticated" }, () => (
            <div onClick={() => signIn()} css={connectToYoutubeStyle}>
              YouTubeと連携する
            </div>
          ))
          .with({ status: "authenticated" }, ({ data }) => (
            <Authenticated session={data} />
          ))
          .exhaustive()}
      </div>
      {session.status === "authenticated" && (
        <div css={logoutButtonStyle} onClick={() => signOut()} role={"button"}>
          ログアウト
        </div>
      )}
    </>
  );
};

const informationContainerStyle = css`
  padding: 1rem;
  border-radius: 0.25rem;
  color: white;
  background: red;
`;

const connectToYoutubeStyle = css`
  border-radius: 1rem;

  &:hover {
    cursor: pointer;
  }
`;

const authenticatedImageUserContainerStyle = css`
  ${flexRow};
  align-items: center;
  gap: 1rem;
`;

const logoutButtonStyle = css`
  margin-top: 1rem;
  color: gray;
  font-size: 12px;

  &:hover {
    cursor: pointer;
  }
`;

const Authenticated = ({ session }: { session: Session }) => {
  const src = session.user?.image;
  const client = useGraphqlClientWithAuth();
  const { channelInfo } = useYoutubeChannelInfo();

  useEffect(() => {
    if (!client) return;
    if (!channelInfo) return;
    const youtubeChannelId = channelInfo.items.at(0)?.id;
    if (!youtubeChannelId) return;

    (async () => {
      await client
        .registerUserYoutubeAccount({ youtubeChannelId })
        .catch(console.error);
    })();
  }, [client, channelInfo]);

  return (
    <div css={authenticatedImageUserContainerStyle}>
      {src && (
        <Image
          src={src}
          alt={"プロフィール画像"}
          width={40}
          height={40}
          priority
        />
      )}
      <p>{session.user?.name}</p>
    </div>
  );
};

export default YouTubeAlignment;
